import React, { useState, useMemo } from 'react';
import {
 ChevronLeft,
 ChevronRight,
 Clock,
 ListChecks,
 Loader2,
 CalendarDays,
 AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/shared';
import { useAdminBookings } from '../../services/adminBookingService';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminCalendar = () => {
 const navigate = useNavigate();
 const [currentDate, setCurrentDate] = useState(new Date());

 // Fetch all bookings for the calendar (all vendors)
 const { data: bookingsResponse, isLoading, error } = useAdminBookings({});
 const allBookings = bookingsResponse?.data?.data || [];
 // Filter out pending bookings
 const bookings = allBookings.filter(booking => booking.status !== 'pending');

 // Get current month and year
 const currentMonth = currentDate.getMonth();
 const currentYear = currentDate.getFullYear();

 // Get first day of month and number of days
 const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
 const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
 const daysInMonth = lastDayOfMonth.getDate();
 const startingDayOfWeek = firstDayOfMonth.getDay();

 // Generate calendar days
 const calendarDays = useMemo(() => {
 const days = [];

 // Add empty cells for days before the first day of the month
 for (let i = 0; i < startingDayOfWeek; i++) {
 days.push(null);
 }

 // Add days of the month
 for (let day = 1; day <= daysInMonth; day++) {
 const date = new Date(currentYear, currentMonth, day);
 const dayBookings = bookings.filter(booking => {
 const bookingDate = new Date(booking.event_date);
 return bookingDate.toDateString() === date.toDateString();
 });

 days.push({
 day,
 date,
 bookings: dayBookings
 });
 }

 return days;
 }, [currentMonth, currentYear, bookings, daysInMonth, startingDayOfWeek]);

 // Navigation functions
 const goToPreviousMonth = () => {
 setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
 };

 const goToNextMonth = () => {
 setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
 };

 const goToToday = () => {
 setCurrentDate(new Date());
 };

 // Get month name
 const monthNames = [
 'January', 'February', 'March', 'April', 'May', 'June',
 'July', 'August', 'September', 'October', 'November', 'December'
 ];

 const getStatusColor = (status) => {
 const colors = {
 pending:
 'bg-amber-50 text-amber-800 border-amber-200',
 confirmed:
 'bg-surface-muted text-ink border-border',
 in_progress:
 'bg-surface-muted text-ink border-border',
 completed:
 'bg-emerald-50 text-emerald-800 border-emerald-200',
 cancelled:
 'bg-red-50 text-red-700 border-red-200',
 no_show:
 'bg-gray-100 text-gray-700 border-gray-200',
 };
 return colors[status] || colors.pending;
 };

 const getBookingDotStyle = (dayBookings) => {
 if (!dayBookings.length) return null;
 const hasLive = dayBookings.some((b) => ['confirmed', 'in_progress'].includes(b.status));
 if (hasLive) {
 return { solid: 'bg-ink', title: 'Active booking on this date' };
 }
 return { solid: 'bg-emerald-500', title: 'Booking on this date' };
 };

 const getStatusLabel = (status) => {
 const labels = {
 pending: 'Pending',
 confirmed: 'Confirmed',
 in_progress: 'In Progress',
 completed: 'Completed',
 cancelled: 'Cancelled',
 no_show: 'No Show'
 };
 return labels[status] || 'Pending';
 };

 const formatTime = (timeString) => {
 if (!timeString) return '';
 const date = new Date(timeString);
 return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
 };

 const isToday = (date) => date.toDateString() === new Date().toDateString();

 const legendStatuses = [
 { status: 'pending', label: 'Pending' },
 { status: 'confirmed', label: 'Confirmed' },
 { status: 'in_progress', label: 'In Progress' },
 { status: 'completed', label: 'Completed' },
 { status: 'cancelled', label: 'Cancelled' },
 { status: 'no_show', label: 'No Show' },
 ];

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="All Bookings Calendar"
 subtitle="See marketplace events by month — click a booking to open details."
 actions={
 <div className="flex items-center gap-2">
 <Button variant="outline" onClick={goToToday} size="sm" className="rounded-md">
 Today
 </Button>
 <div className="flex items-center gap-1.5">
 <button
 type="button"
 onClick={goToPreviousMonth}
 aria-label="Previous month"
 className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface text-ink-secondary hover:bg-surface-muted hover:border-ink transition-colors"
 >
 <ChevronLeft className="w-4 h-4" />
 </button>
 <button
 type="button"
 onClick={goToNextMonth}
 aria-label="Next month"
 className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface text-ink-secondary hover:bg-surface-muted hover:border-ink transition-colors"
 >
 <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.calendar.title}
 steps={ADMIN_PAGE_GUIDES.calendar.steps}
 icon={ListChecks}
 />

 <div className="product-panel overflow-hidden">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5">
 <h2 className="text-lg sm:text-xl font-semibold text-ink tracking-tight">
 {monthNames[currentMonth]} {currentYear}
 </h2>
 <p className="text-xs sm:text-sm text-ink-tertiary">
 {isLoading
 ? 'Loading events…'
 : error
 ? 'Unable to load events'
 : `${bookings.length} event${bookings.length === 1 ? '' : 's'} loaded`}
 </p>
 </div>

 {isLoading ? (
 <div className="py-20 text-center px-4">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading calendar…</p>
 </div>
 ) : error ? (
 <div className="py-16 sm:py-20 text-center px-4">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500">
 <AlertCircle className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 Error loading calendar
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto leading-relaxed">
 Unable to load bookings calendar. Please try again shortly.
 </p>
 </div>
 ) : (
 <>
 <div className="grid grid-cols-7 border-b border-border bg-surface-muted/80">
 {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
 <div
 key={day}
 className="px-2 py-3 text-center text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-ink-tertiary"
 >
 {day}
 </div>
 ))}
 </div>

 <div className="grid grid-cols-7">
 {calendarDays.map((dayData, index) => {
 const today = dayData && isToday(dayData.date);
 return (
 <div
 key={index}
 className={`relative min-h-[100px] sm:min-h-[120px] border-r border-b border-border p-2 sm:p-2.5 overflow-visible transition-colors ${ dayData ? 'bg-surface hover:bg-surface-muted/80' : 'bg-surface-muted/60' } ${(index + 1) % 7 === 0 ? 'border-r-0' : ''}`}
 >
 {dayData && (
 <>
 <div className="mb-2">
 <div className="relative inline-flex">
 <span
 className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full transition-colors ${ today ? 'bg-ink text-white ring-2 ring-ink/20 ' : 'text-ink' }`}
 >
 {dayData.day}
 </span>
 {(() => {
 const dot = getBookingDotStyle(dayData.bookings);
 if (!dot) return null;
 return (
 <span
 className="absolute -top-0.5 -right-0.5 flex h-2 w-2"
 title={dot.title}
 aria-hidden
 >
 <span
 className={`relative inline-flex h-2 w-2 rounded-full ${dot.solid}`}
 />
 </span>
 );
 })()}
 </div>
 </div>

 <div className="space-y-1">
 {dayData.bookings.slice(0, 3).map((booking) => (
 <div
 key={booking.id}
 className={`text-xs p-1.5 sm:p-2 rounded-lg border cursor-pointer hover:shadow-sm hover:brightness-[0.98] transition-all ${getStatusColor(booking.status)}`}
 onClick={() => navigate(`/admin/bookings/${booking.id}`)}
 >
 <div className="flex items-center justify-between gap-1 mb-0.5">
 <div className="flex items-center gap-1 min-w-0">
 <Clock className="w-3 h-3 shrink-0 opacity-70" />
 <span className="font-medium truncate">
 {formatTime(booking.start_time)}
 </span>
 </div>
 <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0 opacity-80">
 {getStatusLabel(booking.status)}
 </span>
 </div>
 <div className="font-mono text-[11px] font-medium truncate">
 #{booking.booking_reference}
 </div>
 <div className="truncate text-[11px] opacity-90">
 {booking.guest_name || booking.client?.name || 'Unknown Client'}
 </div>
 <div className="truncate text-[10px] opacity-70 mt-0.5">
 {booking.vendor?.business_name || 'Unknown Vendor'}
 </div>
 </div>
 ))}

 {dayData.bookings.length > 3 && (
 <div className="text-[11px] text-ink-tertiary text-center py-1 font-medium">
 +{dayData.bookings.length - 3} more
 </div>
 )}
 </div>
 </>
 )}
 </div>
 );
 })}
 </div>

 {!isLoading && !error && bookings.length === 0 && (
 <div className="border-t border-border py-10 text-center px-4">
 <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <CalendarDays className="w-6 h-6" />
 </div>
 <h3 className="text-sm font-semibold text-ink">
 No events this month
 </h3>
 <p className="mt-1 text-sm text-ink-tertiary max-w-sm mx-auto leading-relaxed">
 Bookings for {monthNames[currentMonth]} will appear on the days they are scheduled.
 </p>
 </div>
 )}
 </>
 )}
 </div>

 <div className="product-panel px-4 py-4 sm:px-5">
 <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
 <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary shrink-0">
 Legend
 </h3>
 <div className="flex flex-wrap items-center gap-1.5">
 <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-border bg-surface-muted text-ink-secondary">
 <span className="h-1.5 w-1.5 rounded-full bg-ink" />
 Active
 </span>
 <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-border bg-surface-muted text-ink-secondary">
 <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
 Other
 </span>
 {legendStatuses.map(({ status, label }) => (
 <span
 key={status}
 className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border ${getStatusColor(status)}`}
 >
 {label}
 </span>
 ))}
 </div>
 </div>
 </div>
 </div>
 );
};

export default AdminCalendar;
